import React from 'react'
import { Box, Button, Divider, Grid, List, ListItem, ListItemAvatar, ListItemButton, ListItemText, Paper, Skeleton, styled, Tooltip, Typography, Stack, useMediaQuery } from '@mui/material'
import { useDocumentTitle } from '@uidotdev/usehooks'
import CenterBox from 'components/helpers/CenterBox'
import LazyImage from 'components/shared-components/LazyImage'
import { type ConnectedYahooFantasyPlayer, type ConnectedYahooFantasyTeam } from 'features/fantasy-team/models/yahooConnectedTeam'
import ElectricalServicesIcon from '@mui/icons-material/ElectricalServices'
import { useNavigate } from 'react-router'
import PageBase from 'components/page-title/PageBase'
import { PlayerImage } from 'components/shared-components/PlayerImage'
import MyFantasyPlayerStatistics from './MyFantasyPlayerStatistics'
import { useQuery } from '@tanstack/react-query'
import { ApiQueryKeys, ApiQueryService } from 'services/query/apiQueryService'
import { useSnackbar } from 'hooks/notification/UseSnackbar'
import MyFantasyGoalieStatistics from './MyFantasyGoalieStatistics'
import PlayerCard from './PlayerCard'
import { Add, Group, HealthAndSafety, Home, LocalHospital, CheckCircle, SyncAlt, VerifiedUser } from '@mui/icons-material'
import TeamImage from 'components/shared-components/TeamImage'
import NoDataMessageComponent from 'components/app-no-data/AppNoData'
import { FcInfo } from 'react-icons/fc'

const DisabledPlug = styled(ElectricalServicesIcon)({
  color: 'info',
  fontSize: 'calc(1dvw + 64px)'
})

const MyFantasyTeam: React.FC = React.memo((): JSX.Element => {
  useDocumentTitle('FantaHockey | My Fantasy Team')
  const { openSnackbar } = useSnackbar()
  const [isPlayerStatsDrawerOpen, setIsPlayerStatsDrawerOpen] = React.useState<boolean>(false)
  const [isGoalieStatsDrawerOpen, setIsGoalieStatsDrawerOpen] = React.useState<boolean>(false)
  const [fantasyTeam, setFantasyTeam] = React.useState<ConnectedYahooFantasyTeam>({ teamRank: undefined, teamName: undefined, players: [], goalies: [] })
  const [selectedPlayer, setSelectedPlayer] = React.useState<ConnectedYahooFantasyPlayer>({
    playerId: 0,
    name: '',
    playerHeadshotUrl: '',
    team: '',
    teamLogoUrl: '',
    position: '',
    fantasyScore: 0,
    injuryStatus: '',
    isInjured: false
  } satisfies ConnectedYahooFantasyPlayer)
  const [selectedGoalie, setSelectedGoalie] = React.useState<ConnectedYahooFantasyPlayer>({
    playerId: 0,
    name: '',
    playerHeadshotUrl: '',
    team: '',
    teamLogoUrl: '',
    position: '',
    fantasyScore: 0,
    injuryStatus: '',
    isInjured: false
  } satisfies ConnectedYahooFantasyPlayer)
  const navigate = useNavigate()
  const isDesktop = useMediaQuery('(min-width:1024px)')
  const handleView = React.useCallback((player: ConnectedYahooFantasyPlayer) => { setSelectedPlayer(player); togglePlayerStatsDrawer(true) }, [])

  const { data: isYahooUserConnected } = useQuery<boolean>({
    queryKey: [ApiQueryKeys.IsUserConnectedToYahooQuery],
    queryFn: async () => {
      return (await ApiQueryService[ApiQueryKeys.IsUserConnectedToYahooQuery]()).data
    },
    initialData: false
  })

  const { isError: isErrorFetchingTeam, isFetching: isFetchingYahooTeam, data: yahooFantasyTeam } = useQuery<ConnectedYahooFantasyTeam>({
    queryKey: [ApiQueryKeys.GetConnectedYahooFantasyTeamQuery],
    queryFn: async () => {
      return (await ApiQueryService[ApiQueryKeys.GetConnectedYahooFantasyTeamQuery]()).data
    },
    initialData: { teamRank: undefined, teamName: undefined, players: [], goalies: [] }
  })

  React.useEffect(() => {
    const getFantasyTeam = async (): Promise<void> => {
      try {
        if (yahooFantasyTeam !== undefined) {
          setFantasyTeam(yahooFantasyTeam)
        }
      } catch (error: unknown) {
        openSnackbar('We can not fetch your Yahoo! Fantasy Team at this time.', 'error')
      }
    }
    void getFantasyTeam()
  }, [yahooFantasyTeam])

  const mapPosition = (position: string): string => {
    switch (position) {
      case 'C':
        return 'Center'
      case 'L':
        return 'Left Wing'
      case 'R':
        return 'Right Wing'
      case 'D':
        return 'Defense'
      case 'G':
        return 'Goalie'
      default:
        return ''
    }
  }

  const MyFantasyLoadingListItem = (): JSX.Element => (
    <ListItem alignItems="flex-start">
      <ListItemAvatar>
        <Skeleton variant="circular" width={50} height={50} />
      </ListItemAvatar>
      <Box ml={1} />
      <ListItemText
        primary={
          <>
            <Skeleton variant='text' width='100%' height={20} />
            <Skeleton variant='text' width='100%' height={20} />
            <Box my={1} />
            <Skeleton variant="rectangular" width='100%' height={50} />
          </>
        }
      />
    </ListItem>
  )

  const MyFantasyTeamLoader = (): JSX.Element => (
    <Box p={1} m={1}>
      <List sx={{ bgcolor: 'background.paper', borderRadius: 2 }}>
        <Box p={1} m={1}>
          {Array.from(Array(3).keys()).map((value, index) => (
            <MyFantasyLoadingListItem key={index} />
          ))}
        </Box>
      </List>
    </Box>
  )

  const togglePlayerStatsDrawer = (newOpen: boolean): void => {
    setIsPlayerStatsDrawerOpen(newOpen)
  }

  const toggleGoalieStatsDrawer = (newOpen: boolean): void => {
    setIsGoalieStatsDrawerOpen(newOpen)
  }

  return (
    <>
      <PageBase icon={Group} title='My Fantasy Team'>
        <Box ml={2}>
          <Typography fontFamily={'Spantaran'} variant='h6' color='white'>{fantasyTeam.teamName}</Typography>
        </Box>
        <Box mt={1} />
        {isFetchingYahooTeam && (
          <MyFantasyTeamLoader />
        )}
        {!isFetchingYahooTeam && isErrorFetchingTeam && (
          <Grid item xs={12} sm={12} md={12} xl={12}>
            <CenterBox flexDirection={'column'}>
              <Typography m={4} variant='subtitle1'>
                We can not fetch your Yahoo! Fantasy Team at this time.
              </Typography>
              <DisabledPlug color='info' fontSize={'large'} />
              <Box my={2} />
              <Button variant='outlined' color='success' onClick={() => { navigate('/home') }}>
                <Home /> <Box ml={1} /> Go Home
              </Button>
            </CenterBox>
          </Grid>
        )}
        {!isFetchingYahooTeam && !isErrorFetchingTeam && !isYahooUserConnected && (
          <Grid item xs={12} sm={12} md={12} xl={12}>
            <CenterBox
              flexDirection={'column'}
              sx={{
                mt: { xs: 2, sm: 3 },
                mb: { xs: 2, sm: 3 },
                px: 1.5
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  width: '100%',
                  maxWidth: 980,
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: 4,
                  p: { xs: 2.5, sm: 4 },
                  border: (theme) => `1px solid ${theme.palette.primary.main}55`,
                  background: (theme) =>
                    `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.background.paper} 52%, ${theme.palette.info.dark} 100%)`,
                  boxShadow: (theme) => `0 18px 42px ${theme.palette.primary.main}2a`
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    width: 220,
                    height: 220,
                    borderRadius: '50%',
                    top: -84,
                    right: -54,
                    background: 'radial-gradient(circle, rgba(255,255,255,0.24) 0%, rgba(255,255,255,0) 72%)'
                  }}
                />
                <Grid container spacing={3} alignItems='center'>
                  <Grid item xs={12} md={7}>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        px: 1.5,
                        py: 0.75,
                        mb: 2,
                        borderRadius: 99,
                        border: '1px solid rgba(255,255,255,0.35)',
                        bgcolor: 'rgba(255,255,255,0.08)'
                      }}
                    >
                      <Typography variant='caption' sx={{ color: '#f4f6ff', letterSpacing: 0.4, fontWeight: 700 }}>
                        CONNECT TO UNLOCK YOUR ROSTER
                      </Typography>
                    </Box>
                    <Typography
                      variant='h4'
                      fontFamily={'Spantaran'}
                      sx={{ color: '#ffffff', lineHeight: 1.15, mb: 1.5 }}
                    >
                      Yahoo Fantasy Team Not Connected
                    </Typography>
                    <Typography variant='subtitle1' sx={{ color: '#dce5ff', mb: 2.5, maxWidth: 620 }}>
                      Connect your Yahoo account once to sync your roster, view deeper player stats, and get lineup insights tailored to your league.
                    </Typography>

                    <Stack spacing={1.25} mb={3}>
                      <Stack direction='row' spacing={1.25} alignItems='center'>
                        <CheckCircle sx={{ color: 'success.light' }} />
                        <Typography variant='body2' sx={{ color: '#e8edff' }}>
                          Secure OAuth sign-in with read-only team access
                        </Typography>
                      </Stack>
                      <Stack direction='row' spacing={1.25} alignItems='center'>
                        <SyncAlt sx={{ color: 'info.light' }} />
                        <Typography variant='body2' sx={{ color: '#e8edff' }}>
                          Automatic roster sync and up-to-date availability data
                        </Typography>
                      </Stack>
                      <Stack direction='row' spacing={1.25} alignItems='center'>
                        <VerifiedUser sx={{ color: 'warning.light' }} />
                        <Typography variant='body2' sx={{ color: '#e8edff' }}>
                          Personalized recommendations based on your current squad
                        </Typography>
                      </Stack>
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                      <Button
                        variant='contained'
                        color='success'
                        size='large'
                        onClick={() => { navigate('/connect-my-fantasy-team') }}
                        startIcon={<Add />}
                        sx={{ px: 2.5 }}
                      >
                        Connect Yahoo Account
                      </Button>
                      <Button
                        variant='outlined'
                        color='inherit'
                        size='large'
                        onClick={() => { navigate('/connect-my-fantasy-team') }}
                        sx={{ borderColor: 'rgba(255,255,255,0.4)', color: '#f3f6ff' }}
                      >
                        View Connection Steps
                      </Button>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Paper
                      elevation={0}
                      sx={{
                        height: '100%',
                        borderRadius: 3,
                        px: { xs: 2, sm: 3 },
                        py: { xs: 2, sm: 2.5 },
                        bgcolor: 'rgba(12, 24, 52, 0.56)',
                        border: '1px solid rgba(255,255,255,0.18)'
                      }}
                    >
                      <CenterBox flexDirection='column' sx={{ textAlign: 'center', gap: 1.5 }}>
                        <LazyImage src={'./assets/images/Yahoo!-Logo.wine.png'} alt={'Yahoo'} height={'calc(90px + 0.6vw)'} />
                        <Typography variant='h6' fontFamily='Spantaran' sx={{ color: '#ffffff' }}>
                          Quick and Secure Setup
                        </Typography>
                        <Typography variant='body2' sx={{ color: '#dbe4ff' }}>
                          Takes less than a minute. You can disconnect from Yahoo anytime in your account settings.
                        </Typography>
                      </CenterBox>
                    </Paper>
                  </Grid>
                </Grid>
              </Paper>
            </CenterBox>
          </Grid>
        )}
        <MyFantasyPlayerStatistics
          onOpen={() => { togglePlayerStatsDrawer(true) }}
          open={isPlayerStatsDrawerOpen}
          player={selectedPlayer}
          onClose={() => { togglePlayerStatsDrawer(false) }}
        />
        <Grid container>
        { isYahooUserConnected && !isFetchingYahooTeam && fantasyTeam.players.length > 0 && (
          <Grid item p={1} m={1} xs={12} sm={12} md={12} xl={12}>
            <Typography fontFamily={'Spantaran'} ml={1} variant='h6'>Players</Typography>
            {isDesktop
              ? (
              <Box
                role="grid"
                aria-label="players-grid"
                aria-colcount={3}
                data-grid-columns="3"
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  gap: 2,
                  alignItems: 'stretch'
                }}
              >
                {fantasyTeam?.players.map((player) => (
                  <PlayerCard
                    key={player.playerId}
                    player={player}
                    onView={() => { handleView(player) }}
                  />
                ))}
              </Box>
                )
              : (
              <Paper elevation={3}>
                <List sx={{ justifyContent: 'space-between', width: '100%', borderRadius: 2 }}>
                  {fantasyTeam?.players.map((player, index: number) => (
                    <React.Fragment key={player.playerId}>
                      <ListItemButton sx={{ m: 0, p: 0 }} onClick={() => { togglePlayerStatsDrawer(true); setSelectedPlayer(player) }}>
                        <ListItem>
                          <ListItemAvatar>
                            <TeamImage src={player.teamLogoUrl} />
                            <PlayerImage src={player.playerHeadshotUrl} />
                          </ListItemAvatar>
                          <Box mr={2} />
                          <ListItemText primary={<Typography fontWeight={700}>{player.name}</Typography>}
                            secondary={<>
                              {mapPosition(player.position)}
                              <Box my={2} />
                            </>}
                          />
                           {player.isInjured &&
                              <Tooltip title={`${player.name} is Injured`}>
                                <LocalHospital color='error' />
                              </Tooltip>}
                            {!player.isInjured &&
                             <Tooltip title={`${player.name} is Healthy`}>
                            <HealthAndSafety color='success' />
                            </Tooltip>}
                        </ListItem>
                      </ListItemButton>
                      {fantasyTeam.players.length - 1 !== index && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
                )}
            <MyFantasyGoalieStatistics
              onOpen={() => { toggleGoalieStatsDrawer(true) }}
              open={isGoalieStatsDrawerOpen}
              goalie={selectedGoalie}
              onClose={() => { toggleGoalieStatsDrawer(false) }}
            />
              <Box my={1}/>
              <Typography fontFamily={'Spantaran'} ml={1} variant='h6'>Goalies</Typography>
              <Paper elevation={3}>
                <List sx={{ justifyContent: 'space-between', width: '100%', borderRadius: 2 }}>
                  {fantasyTeam.goalies.map((goalie, index: number) => (
                    <React.Fragment key={goalie.playerId}>
                      <ListItemButton sx={{ m: 0, p: 0 }} onClick={() => { toggleGoalieStatsDrawer(true); setSelectedGoalie(goalie) }}>
                        <ListItem>
                          <ListItemAvatar>
                            <TeamImage src={goalie.teamLogoUrl} />
                            <PlayerImage src={goalie.playerHeadshotUrl} />
                          </ListItemAvatar>
                          <Box mr={2} />
                          <ListItemText primary={<Typography fontWeight={700}>{goalie.name}</Typography>}
                            secondary={<>
                              {mapPosition(goalie.position)}
                              <Box my={2} />
                              <Box display='flex' flexDirection='row' alignItems={'center'} justifyContent={'center'}>
                              </Box>
                            </>} />
                          {goalie.isInjured &&
                            <Tooltip title={`${goalie.name} is injured`}>
                              <LocalHospital color='error' />
                            </Tooltip>}
                          {!goalie.isInjured &&
                           <Tooltip title={`${goalie.name} is healthy`}>
                          <HealthAndSafety color='success' />
                          </Tooltip>}
                        </ListItem>
                      </ListItemButton>
                      {fantasyTeam.goalies.length - 1 !== index && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
          </Grid>
        )}
        </Grid>
        <Grid container>
        { isYahooUserConnected && !isFetchingYahooTeam && fantasyTeam.players.length === 0 && (
          <Grid item p={1} m={1} xs={12} sm={12} md={12} xl={12}>
            <NoDataMessageComponent message="No players are currently on your Yahoo! fantasy team at this time" icon={FcInfo({ size: 'calc(36px + 1vw)' })} />
            <Box my={2} />
            <Typography variant='caption' color='text.secondary'>If you recently created your Yahoo! fantasy team or have recently started a new season, it may take some time for your players to appear here.
            Please check back later after your team has been fully set up on Yahoo! Fantasy Sports.</Typography>
          </Grid>
        )}
        </Grid>
      </PageBase>
    </>
  )
})

MyFantasyTeam.displayName = 'MyFantasyTeam'
export default MyFantasyTeam
